"use client";
import * as React from "react";
import { Grid, Typography, Link } from "@mui/material";
import Image from "next/image";
import { pxToVw } from "@/utils/pxToVw";
import { unitToVw } from "@/utils/unitToVw";
import { MOBILE_BASE } from "@/theme/theme";
import CustomAccordion, { StyledAccordionDetails } from "@/components/CustomAccordion";

// 👉 usa el nuevo tipo con Contenido (paragraph/image)
import type { NoticiasItem } from "@/types/noticies"; // id, documentId, Titulo, Contenido
import { useText } from "@/context/TextContext";

import Script from "next/script";

type Props = {
  Noticies: NoticiasItem[];
  expandFirst?: boolean;
  /** Si true, corta el contenido de cada noticia justo después del primer isCaptionLike */
  preview?: boolean;
};

// Función para convertir título a slug (formato URL-friendly)
const titleToSlug = (title: string): string => {
  return title
    .toLowerCase()
    .normalize("NFD") // Descomponer caracteres con acentos
    .replace(/[\u0300-\u036f]/g, "") // Eliminar marcas diacríticas
    .replace(/[^a-z0-9\s-]/g, "") // Eliminar caracteres especiales
    .trim()
    .replace(/\s+/g, "-") // Reemplazar espacios con guiones
    .replace(/-+/g, "-"); // Eliminar guiones duplicados
};

export default function NoticiesSection({
  Noticies,
  expandFirst = false,
  preview = false,
}: Props) {
  const [expandedPanel, setExpandedPanel] = React.useState<string | false>(
    expandFirst ? "panel-0" : false
  );

  React.useEffect(() => {
    // Detectar hash en la URL al montar o cuando cambia
    const hash = window.location.hash.slice(1); // Remover el #
    if (hash) {
      // Buscar la noticia que coincida con el hash
      const index = Noticies.findIndex(
        (noticia) => titleToSlug(noticia.Titulo) === hash
      );
      if (index !== -1) {
        const panelId = `panel-${index}`;
        setExpandedPanel(panelId);
        // Hacer scroll después de un pequeño delay para asegurar que el accordion se ha expandido
        setTimeout(() => {
          const element = document.getElementById(hash);
          if (element) {
            const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
            const isMobileView = window.innerWidth < 900; // breakpoint md
            const offsetPosition = elementPosition - (isMobileView ? unitToVw(150, MOBILE_BASE) : unitToVw(400));
            window.scrollTo({
              top: offsetPosition,
              behavior: "smooth"
            });
          }
        }, 300);
      }
    }
  }, [Noticies]);

  const handleAccordionChange =
    (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpandedPanel(newExpanded ? panel : false);
    };

  // helpers mínimos
  const joinText = (children: Array<{ text?: string }> = []) =>
    children
      .map((c) => c?.text ?? "")
      .join("")
      .trim();

  const stripHtml = (s: string) => s.replace(/<[^>]*>/g, "").trim();

  // Detecta si un bloque de tipo "paragraph" es "caption-like"
  const isCaptionLikeBlock = (b: any) => {
    if (!b || b.type !== "paragraph") return false;
    const raw = joinText(b.children as any);
    if (!raw) return false;
    // mismo criterio que en el render
    return (
      (b.children ?? []).some((c: any) => c?.code) &&
      /imageDescription/.test(raw)
    );
  };

  const leermastext = useText("general.llegirmes");
  const noticiasurl = useText("header.noticies.url");

  return (
    <Grid container flexDirection={"column"}>
      <Grid
        container
        paddingTop={{ xs: pxToVw(30, MOBILE_BASE), md: pxToVw(65) }}
        size={{ xs: 12, md: 9 }}
      >
        {Noticies.map((noticia, idx) => {
          const number = String(idx + 1).padStart(2, "0");

          // fechas en formato ISO (Strapi ya las da así normalmente)
          const datePublished = noticia.createdAt || undefined;
          const dateModified = noticia.updatedAt || datePublished;

          const newsSchema = {
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            headline: noticia.Titulo,        // ((Titulo))
            datePublished,                   // ((createdAt))
            dateModified,                    // ((updatedAt))
            author: {
              "@type": "Organization",
              name: "Casa Gay",
            },
            publisher: {
              "@type": "Organization",
              name: "Casa Gay",
              logo: {
                "@type": "ImageObject",
                url: "https://strapi.casagay.com/uploads/logo_casagay_b3ba925aaf.svg",
              },
            },
          };

          // Decide qué bloques renderizar en función de "preview"
          let blocks = (noticia.Contenido ?? []) as any[];
          let cutAt = -1;
          let showReadMore = false;

          if (preview) {
            cutAt = blocks.findIndex(isCaptionLikeBlock);
            if (cutAt >= 0) {
              showReadMore = cutAt + 1 < blocks.length; // hay más contenido tras el corte
              blocks = blocks.slice(0, cutAt + 1); // incluye el primer caption-like
            }
          }

          const panelId = `panel-${idx}`;
          const slugId = titleToSlug(noticia.Titulo);

          return (
            <React.Fragment key={(noticia.documentId || noticia.Titulo) + idx}>
              <Script
                id={`news-schema-${noticia.documentId || idx}`}
                type="application/ld+json"
                strategy="afterInteractive"
                dangerouslySetInnerHTML={{
                  __html: JSON.stringify(newsSchema),
                }}
              />

              <CustomAccordion
                id={slugId}
                number={number}
                title={noticia.Titulo}
                titleVariant="body1"
                titleComponent="h2"
                expanded={expandedPanel === panelId}
                onChange={handleAccordionChange(panelId)}
              >
                <StyledAccordionDetails>
                  {blocks.map((b, bIdx) => {
                    if (b.type === "image") {
                      const { url, width, height, caption, name } = b.image;
                      return (
                        <React.Fragment key={`img-${idx}-${bIdx}`}>
                          <Grid
                            container
                            size={{ xs: 10.23, md: 7.453 }}
                            ml={{ xs: 0, md: pxToVw(125) }}
                            mb={{ xs: pxToVw(5, MOBILE_BASE), md: pxToVw(5) }}
                            mt={{ xs: 0, md: bIdx > 0 ? pxToVw(36) : 0 }}
                          >
                            <Image
                              alt={name || noticia.Titulo}
                              src={url}
                              width={width}
                              height={height}
                              style={{ width: "100%", height: "auto" }}
                            />
                          </Grid>

                          {caption && (
                            <Grid
                              ml={{ xs: 0, md: pxToVw(126) }}
                              container
                              flexDirection={"column"}
                              spacing={pxToVw(10)}
                              mb={{ xs: pxToVw(20, MOBILE_BASE), md: pxToVw(10) }}
                            >
                              <Typography
                                variant="body5"
                                fontSize={{
                                  xs: pxToVw(12, MOBILE_BASE),
                                  md: pxToVw(16),
                                }}
                                lineHeight={{
                                  xs: pxToVw(14.4, MOBILE_BASE),
                                  md: pxToVw(18.4),
                                }}
                                color="warmaa"
                                maxWidth={{ xs: "90%", md: "100%" }}
                              >
                                {caption}
                              </Typography>
                            </Grid>
                          )}
                        </React.Fragment>
                      );
                    }

                    // paragraph
                    const raw = joinText(b.children as any);
                    if (!raw) return null;

                    const isCaptionLike =
                      (b.children ?? []).some((c: any) => c?.code) &&
                      /imageDescription/.test(raw);

                    const prevIsCaptionLike =
                      bIdx > 0 && isCaptionLikeBlock(blocks[bIdx - 1]);

                    const text = isCaptionLike ? stripHtml(raw) : raw;

                    return (
                      <Typography
                        key={`p-${idx}-${bIdx}`}
                        component="p"
                        variant={isCaptionLike ? "body5" : "body2"}
                        color={isCaptionLike ? "warmaa" : "marron"}
                        style={
                          !isCaptionLike && bIdx === 1
                            ? { textIndent: pxToVw(40) }
                            : undefined
                        }
                        sx={{
                          mb: { xs: pxToVw(10, MOBILE_BASE), md: pxToVw(10) },
                          ml: isCaptionLike ? { xs: 0, md: pxToVw(126) } : 0,
                          maxWidth: isCaptionLike
                            ? { xs: "90%", md: "100%" }
                            : "100%",
                          fontSize: isCaptionLike
                            ? { xs: pxToVw(12, MOBILE_BASE), md: pxToVw(16) }
                            : undefined,
                          lineHeight: isCaptionLike
                            ? { xs: pxToVw(14.4, MOBILE_BASE), md: pxToVw(18.4) }
                            : undefined,
                          mt:
                            !isCaptionLike && prevIsCaptionLike
                              ? { xs: pxToVw(70, MOBILE_BASE), md: pxToVw(70) }
                              : undefined,
                        }}
                      >
                        {text}
                      </Typography>
                    );
                  })}
                  {showReadMore && (
                    <Grid ml={{ xs: 0, md: pxToVw(126) }} container>
                      <Typography
                        variant="label1"
                        color="warmaa"
                        sx={{
                          mt: { xs: pxToVw(10, MOBILE_BASE), md: pxToVw(10) },
                        }}
                      >
                        <Link
                          href={`${noticiasurl}#${slugId}`} 
                          style={{ textDecoration: "none", color: "inherit" }}
                        >
                          {leermastext} →
                        </Link>
                      </Typography>
                    </Grid>
                  )}
                </StyledAccordionDetails>
              </CustomAccordion>
            </React.Fragment>
          );
        })}
      </Grid>
    </Grid>
  );
}
